import { Form, FormSpy } from 'react-final-form';
import { FieldArray } from 'react-final-form-arrays';
import arrayMutators from 'final-form-arrays';
import { Button } from 'react-bootstrap';
import useApi from 'common/hooks/useApi';
import formatDateFromMillis from 'common/services/date/formatDate';
import DisplayAddress from 'domain/components/address/DisplayAddress';
import ApplicationDetail from 'domain/types/ApplicationDetail';
import ChsIntermediary from 'domain/types/ChsIntermediary';
import { DetailRow } from './DetailRow';
import { RelationshipToProperty } from './RelationshipToProperty';
import { ActionCompleteResponse } from 'common/types/ApiResponse';
import FinalFormField from 'common/components/final-form/FinalFormField';
import DwellingTypes from 'domain/enums/DwellingTypes';
import useCodeTable from 'common/hooks/useCodeTable';
import { ValidationObj } from 'common/types/ValidationObj';
import { useContext, useState } from 'react';
import { GroupDetailsContext } from './GroupDetailsContainer';
import useTypedSelector from 'common/hooks/useTypedSelector';
import MessageResult from './MessageResult';
import IntermediariesList from 'common/components/intermediaries-list/IntermediaryList';

type ViewProps = {
  details: ApplicationDetail;
  intermediaries: ChsIntermediary[];
  canResubmit: boolean;
  onResubmitClick: null | (() => void);
};

type FormData = {
  dwellingTypeCd: string | null;
  intermediaries: ChsIntermediary[];
  howManyRooms: number | null;
};

export function GroupDetailForm(props: ViewProps) {
  const { name } = useTypedSelector((state) => ({
    name: `${state.profile.data.firstName} ${state.profile.data.lastName}`,
  }));
  const { intermediaries, details, canResubmit, onResubmitClick } = props;
  const [saveOk, setSaveOk] = useState<boolean | null>(null);

  const intermediaryCodes = useCodeTable({
    endpoint: '/intermediaries',
  });
  const dwellingCodes = useCodeTable({
    endpoint: '/dwellingTypes',
  });

  const [{ loading: saving }, save] = useApi<ActionCompleteResponse>({
    url: `/applications/groups/${details.groupRefKey}`,
    method: 'PUT',
  });

  const mappedIntermediaryCodes = intermediaryCodes.map((opt) => ({
    value: opt.cd,
    label: opt.descr,
  }));

  const canEdit = details.allowEdit;

  const groupDetailsContext = useContext(GroupDetailsContext);

  return (
    <>
      <Form<FormData>
        onSubmit={(values) => {
          save({
            interMediaryCds: values.intermediaries.map((i) => i.cd),
            dwellingTypeCd: values.dwellingTypeCd,
            howManyRooms: values.howManyRooms,
          })
            .then(() => {
              groupDetailsContext.onRefresh();
              setSaveOk(true);
            })
            .catch(() => {
              setSaveOk(false);
            });
        }}
        validate={(values) => {
          const errors: ValidationObj = {
            intermediaries: undefined,
            howManyRooms: undefined,
          };
          if (
            !values.intermediaries ||
            (values.intermediaries &&
              values.intermediaries.filter((i) => Boolean(i)).length === 0)
          ) {
            errors.intermediaries = [
              'You must select at least one intermediary.',
            ];
          }

          if (
            (values.dwellingTypeCd === DwellingTypes.COMBINATION ||
              values.dwellingTypeCd === DwellingTypes.PARTIAL) &&
            !values.howManyRooms
          ) {
            errors.howManyRooms =
              'You must enter the number of rooms you are listing.';
          }
          return errors;
        }}
        mutators={{ ...arrayMutators }}
        initialValues={{
          intermediaries,
          dwellingTypeCd: details.dwellingTypeCd,
          howManyRooms: details.howManyRooms,
        }}
      >
        {({ handleSubmit, dirty }) => (
          <form onSubmit={handleSubmit}>
            {details.registrationNum && (
              <DetailRow
                left='Registration Number'
                right={details.registrationNum}
              />
            )}
            <DetailRow left='Name' right={name} />
            <DetailRow
              left='Address'
              right={<DisplayAddress address={details.address} />}
            />
            <DetailRow left='Email address' right={details.email} />
            <DetailRow
              left='Relationship to property'
              right={
                <RelationshipToProperty
                  cd={details.propertyRelationshipCd}
                  descr={details.propertyRelationshipDescr}
                  attachments={details.proofOfResidence}
                />
              }
            />
            <DetailRow
              left='Primary Residence'
              right={details.primaryResIndicator === 'Y' ? 'Yes' : 'No'}
            />
            {canEdit ? (
              <DetailRow
                left='Intermediaries'
                right={
                  <FieldArray name='intermediaries'>
                    {({ fields, fields: { value }, meta }) => (
                      <>
                        <IntermediariesList
                          options={mappedIntermediaryCodes}
                          selectedValues={value}
                          onSelect={(selectedValue) => {
                            const opt = mappedIntermediaryCodes.find(
                              (c) => c.value === selectedValue
                            );
                            fields.push({
                              cd: opt?.value,
                              descr: opt?.label,
                            });
                          }}
                          onRemove={(selectedValue) => {
                            const removeIdx = value.findIndex(
                              (c) => c.cd === selectedValue
                            );
                            if (removeIdx !== -1) {
                              fields.remove(removeIdx);
                            } else {
                              console.error(
                                'Item cannot be removed. Index not found.'
                              );
                            }
                          }}
                        />
                        {meta.error && meta.touched && (
                          <div className={`d-block invalid-feedback`}>
                            {meta.error}
                          </div>
                        )}
                      </>
                    )}
                  </FieldArray>
                }
              ></DetailRow>
            ) : (
              <DetailRow
                left={'Intermediaries'}
                right={details.intermediaries.map((i) => i.descr).join(',')}
              />
            )}

            {canEdit ? (
              <DetailRow
                left='Room Type'
                right={
                  <FinalFormField
                    name='dwellingTypeCd'
                    label=''
                    type='select'
                    options={dwellingCodes.filter((d) => d.cd !== 'NONE')}
                    when={{
                      field: 'dwellingTypeCd',
                      becomes: DwellingTypes.ENTIRE,
                      set: 'howManyRooms',
                      to: null,
                    }}
                  />
                }
              ></DetailRow>
            ) : (
              <DetailRow left={'Room Type'} right={details.dwellingTypeDescr} />
            )}
            <DetailRow
              left={
                <>
                  Total number of sleeping
                  <br /> rooms in entire unit
                </>
              }
              right={details.numberOfRooms}
            />
            <FormSpy>
              {({ values }) => (
                <>
                  {(values.dwellingTypeCd === DwellingTypes.COMBINATION ||
                    values.dwellingTypeCd === DwellingTypes.PARTIAL) && (
                    <DetailRow
                      left='Total rooms you are listing'
                      right={
                        <FinalFormField
                          type='number'
                          name='howManyRooms'
                          label=''
                        />
                      }
                    />
                  )}
                </>
              )}
            </FormSpy>
            <DetailRow
              left='Date submitted'
              right={formatDateFromMillis(details.submittedDate)}
            />
            <DetailRow left='Status' right={details.clientFacingStatusDescr} />

            <MessageResult
              result={saveOk}
              messageSuccess='Application changes successfully saved.'
              messageFailure='Error while saving Application.'
              onClose={() => setSaveOk(null)}
            />

            <div style={{ marginTop: '50px' }}>
              {canResubmit && onResubmitClick && (
                <Button
                  variant='outline-primary'
                  className='float-left ml-4'
                  onClick={onResubmitClick}
                >
                  Re-submit Application
                </Button>
              )}

              {canEdit && dirty && (
                <Button
                  variant='primary'
                  type='submit'
                  disabled={saving}
                  className='float-right'
                >
                  Save changes
                </Button>
              )}
            </div>
          </form>
        )}
      </Form>
    </>
  );
}
